import { lazy, ComponentType } from 'react';

/**
 * A wrapper for React.lazy that retries the import if it fails.
 * This is particularly useful for handling ChunkLoadErrors that occur 
 * when a new version of the app is deployed and old chunks are removed 
 * from the server, or due to intermittent network issues.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
    componentImport: () => Promise<{ default: T }>
) {
    return lazy(async () => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
        );

        try {
            const component = await componentImport();
            window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
            return component;
        } catch (error: any) {
            if (!pageHasAlreadyBeenForceRefreshed) {
                // First failure: set flag and force refresh
                window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
                window.location.reload();
                // Return a never-resolving promise while the page reloads
                return new Promise<{ default: T }>(() => { });
            }

            // Second failure: Let the ErrorBoundary handle it
            throw error;
        }
    });
}
